import {
  DeleteTimeOffType,
  DeleteTimeOffTypeRate,
  GetTimeOffTypes,
  PostTimeOffTypeRate,
  PostTimeOffType,
  PostTimeOffTypeSortOrder,
  PutTimeOffType,
} from "@/server-types";
import { del, get, post, put } from "../request-helpers";
import { useCustomQuery } from "../custom-query";
import { useCustomMutation } from "../custom-mutation";
import { describeErrorForLog } from "@/utils/sentry-error";
import { sentryLog } from "@/utils/sentry-utils";

/**
 * GET /time-off-type
 */
export const useGetTimeOffTypes = (
  query: GetTimeOffTypes["query"] & { enabled?: boolean } = {},
) => {
  return useCustomQuery({
    queryKey: ["timeOffTypes", query],
    queryFn: () => get<GetTimeOffTypes>("/time-off-type", query),
    enabled: query.enabled,
  });
};

/**
 * POST /time-off-type
 */
export const usePostTimeOffType = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationKey: ["timeOffTypes"],
    mutationFn: (body: PostTimeOffType["body"]) =>
      post<PostTimeOffType>("/time-off-type", body),
    onError: (error) =>
      sentryLog.error(
        "Time-off type create failed",
        describeErrorForLog(error),
      ),
  });
};

/**
 * PUT /time-off-type/:id
 */
export const usePutTimeOffType = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationKey: ["timeOffTypes"],
    mutationFn: ({ id, body }: { id: number; body: PutTimeOffType["body"] }) =>
      put<PutTimeOffType>(`/time-off-type/${id}`, body),
    onError: (error) =>
      sentryLog.error(
        "Time-off type update failed",
        describeErrorForLog(error),
      ),
  });
};

/**
 * POST /time-off-type/sort-order
 */
export const usePostTimeOffTypeSortOrder = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationKey: ["timeOffTypes"],
    mutationFn: (typeIds: number[]) =>
      post<PostTimeOffTypeSortOrder>("/time-off-type/sort-order", {
        typeIds,
      }),
    onError: (error) =>
      sentryLog.error(
        "Time-off type reorder failed",
        describeErrorForLog(error),
      ),
  });
};

/**
 * DELETE /time-off-type/:id
 */
export const useDeleteTimeOffType = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationKey: ["timeOffTypes"],
    mutationFn: (id: number) => del<DeleteTimeOffType>(`/time-off-type/${id}`),
    onError: (error) =>
      sentryLog.error(
        "Time-off type delete failed",
        describeErrorForLog(error),
      ),
  });
};

/**
 * POST /time-off-type/:id/rate
 */
export const usePostTimeOffTypeRate = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationKey: ["timeOffTypes"],
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: PostTimeOffTypeRate["body"];
    }) => post<PostTimeOffTypeRate>(`/time-off-type/${id}/rate`, body),
    onError: (error) =>
      sentryLog.error(
        "Time-off type rate save failed",
        describeErrorForLog(error),
      ),
  });
};

/**
 * DELETE /time-off-type/:id/rate/:rateId
 */
export const useDeleteTimeOffTypeRate = () => {
  return useCustomMutation({
    displayNotification: true,
    mutationKey: ["timeOffTypes"],
    mutationFn: ({ id, rateId }: { id: number; rateId: number }) =>
      del<DeleteTimeOffTypeRate>(`/time-off-type/${id}/rate/${rateId}`),
    onError: (error) =>
      sentryLog.error(
        "Time-off type rate delete failed",
        describeErrorForLog(error),
      ),
  });
};
